import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Typography,
  Chip,
  Tooltip,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { get, post, put, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const DietPromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewModal, setViewModal] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState(null);
  const [selectedDietType, setSelectedDietType] = useState("");
  const [selectedPhase, setSelectedPhase] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    promptName: "",
    description: "",
    promptTemplate: "",
    applicableDiets: ["traditional", "low_fodmap"],
    applicablePhases: [],
    priority: 1,
    isEnabled: true,
  });



  useEffect(() => {
    fetchPrompts();
  }, [currentPage, searchTerm, selectedDietType, selectedPhase]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      let url = `/api/dashboard/diet-prompts?page=${currentPage}&limit=10&search=${searchTerm}`;
      if (selectedDietType) {
        url += `&dietType=${selectedDietType}`;
      }
      if (selectedPhase) {
        url += `&phase=${selectedPhase}`;
      }

      const response = await get(url);

      if (response?.success) {
        setPrompts(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
      toastMessage("Failed to fetch prompts", "error");
    } finally {
      setLoading(false);
    }
  };



  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.promptName || !formData.promptTemplate) {
        toastMessage("Please fill all required fields", "error");
        return;
      }

      // Smart phase logic validation
      if (formData.applicableDiets.includes("traditional") && !formData.applicableDiets.includes("low_fodmap")) {
        // Traditional diet only - phases should be empty
        if (formData.applicablePhases.length > 0) {
          toastMessage("Traditional diet prompts don't use phases. Phases will be cleared.", "warning");
          formData.applicablePhases = [];
        }
      }

      if (editingPrompt) {
        const response = await put(`/api/dashboard/diet-prompts/${editingPrompt._id}`, formData);
        if (response?.success) {
          toastMessage("Prompt updated successfully", "success");
        }
      } else {
        const response = await post("/api/dashboard/diet-prompts", formData);
        if (response?.success) {
          toastMessage("Prompt created successfully", "success");
        }
      }
      closeModal();
      fetchPrompts();
    } catch (error) {
      console.error("Error submitting prompt:", error);
      toastMessage("Failed to submit prompt", "error");
    }
  };

  const handleDelete = async (promptId) => {
    if (window.confirm("Are you sure you want to delete this prompt?")) {
      try {
        const response = await del(`/api/dashboard/diet-prompts/${promptId}`);
        if (response?.success) {
          toastMessage("Prompt deleted successfully", "success");
          fetchPrompts();
        }
      } catch (error) {
        console.error("Error deleting prompt:", error);
        toastMessage("Failed to delete prompt", "error");
      }
    }
  };

  const handleToggleStatus = async (promptId) => {
    try {
      const response = await put(`/api/dashboard/diet-prompts/${promptId}/toggle`);
      if (response?.success) {
        toastMessage(`Prompt ${response.data.isEnabled ? 'enabled' : 'disabled'} successfully`, "success");
        fetchPrompts();
      }
    } catch (error) {
      console.error("Error toggling prompt status:", error);
      toastMessage("Failed to toggle prompt status", "error");
    }
  };

  const openEditModal = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      promptName: prompt.promptName,
      description: prompt.description,
      promptTemplate: prompt.promptTemplate,
      applicableDiets: prompt.applicableDiets || ["traditional", "low_fodmap"],
      applicablePhases: prompt.applicablePhases || [],
      priority: prompt.priority || 1,
      isEnabled: prompt.isEnabled,
    });
    setOpenModal(true);
  };

  const openViewModal = (prompt) => {
    setViewingPrompt(prompt);
    setViewModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setViewModal(false);
    setEditingPrompt(null);
    setViewingPrompt(null);
    setFormData({
      promptName: "",
      description: "",
      promptTemplate: "",
      applicableDiets: ["traditional", "low_fodmap"],
      applicablePhases: [],
      priority: 1,
      isEnabled: true,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">Diet Prompt Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
          >
            Add New Prompt
          </Button>
        </Box>

         {/* System Overview */}
         <Paper sx={{ p: 2, mb: 3, backgroundColor: "#f8f9fa" }}>
           <Typography variant="h6" gutterBottom>
             🎯 How Diet Prompts Work
           </Typography>
           <Grid container spacing={2}>
             <Grid item xs={12} md={6}>
               <Typography variant="body2" color="textSecondary">
                 <strong>Traditional Diet:</strong> Prompts work for all users without phase restrictions. 
                 Set applicable phases to empty array.
               </Typography>
             </Grid>
             <Grid item xs={12} md={6}>
               <Typography variant="body2" color="textSecondary">
                 <strong>Low FODMAP Diet:</strong> Prompts can be phase-specific (elimination, introduction, maintenance) 
                 for targeted guidance based on user progress.
               </Typography>
             </Grid>
           </Grid>
           
           {/* Parallel System Info */}
           <Alert severity="info" sx={{ mt: 2 }}>
             <Typography variant="body2">
               <strong>🔄 Parallel System:</strong> New prompts from this admin panel work alongside your existing backend prompts. 
               Existing prompts remain unchanged and continue to work as before. New prompts provide enhanced, targeted guidance 
               that can be enabled/disabled without affecting the core system.
             </Typography>
           </Alert>
           
           {/* System Status */}
           <Box sx={{ mt: 2, p: 2, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e0e0e0" }}>
             <Typography variant="subtitle2" gutterBottom>
               📊 System Status
             </Typography>
             <Grid container spacing={2}>
               <Grid item xs={12} md={4}>
                 <Box sx={{ textAlign: "center" }}>
                   <Typography variant="h6" color="primary">
                     {prompts.filter(p => p.isEnabled).length}
                   </Typography>
                   <Typography variant="caption" color="textSecondary">
                     Admin Prompts Active
                   </Typography>
                 </Box>
               </Grid>
               <Grid item xs={12} md={4}>
                 <Box sx={{ textAlign: "center" }}>
                   <Typography variant="h6" color="secondary">
                     {prompts.filter(p => p.applicableDiets?.includes("low_fodmap")).length}
                   </Typography>
                   <Typography variant="caption" color="textSecondary">
                     Low FODMAP Prompts
                   </Typography>
                 </Box>
               </Grid>
               <Grid item xs={12} md={4}>
                 <Box sx={{ textAlign: "center" }}>
                   <Typography variant="h6" color="success">
                     {prompts.filter(p => p.applicableDiets?.includes("traditional")).length}
                   </Typography>
                   <Typography variant="caption" color="textSecondary">
                     Traditional Diet Prompts
                   </Typography>
                 </Box>
               </Grid>
             </Grid>
             <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1, textAlign: "center" }}>
               💡 These prompts work alongside your existing backend system - no changes to current functionality
             </Typography>
           </Box>
         </Paper>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
          <TextField
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Diet Type</InputLabel>
            <Select
              value={selectedDietType}
              onChange={(e) => setSelectedDietType(e.target.value)}
              label="Diet Type"
            >
              <MenuItem value="">All Diets</MenuItem>
              <MenuItem value="traditional">Traditional</MenuItem>
              <MenuItem value="low_fodmap">Low FODMAP</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Phase</InputLabel>
            <Select
              value={selectedPhase}
              onChange={(e) => setSelectedPhase(e.target.value)}
              label="Phase"
            >
              <MenuItem value="">All Phases</MenuItem>
              <MenuItem value="elimination">Elimination</MenuItem>
              <MenuItem value="introduction">Introduction</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="none">No Phase</MenuItem>
            </Select>
          </FormControl>
                     <Button
             variant="outlined"
             onClick={() => {
               setSearchTerm("");
               setSelectedDietType("");
               setSelectedPhase("");
             }}
             sx={{ minWidth: 100 }}
           >
             Reset
           </Button>
        </Box>

        {/* Prompts Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                     <TableCell>Diet Types</TableCell>
                     <TableCell>Phases</TableCell>
                     <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.map((prompt) => (
                <TableRow key={prompt._id}>
                  <TableCell>{prompt.promptName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {prompt.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {prompt.applicableDiets?.map((diet) => (
                        <Chip
                          key={diet}
                          label={diet}
                          size="small"
                          color={diet === "low_fodmap" ? "primary" : "secondary"}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                                        <TableCell>
                        <Tooltip title={
                          prompt.applicableDiets?.includes("traditional") && !prompt.applicableDiets?.includes("low_fodmap")
                            ? "Traditional diet prompts work for all users (no phase restrictions)"
                            : prompt.applicablePhases?.length > 0
                            ? `Phase-specific prompt for: ${prompt.applicablePhases.join(", ")}`
                            : "Works for all phases"
                        }>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {prompt.applicablePhases?.length > 0 ? (
                              prompt.applicablePhases.map((phase) => (
                                <Chip
                                  key={phase}
                                  label={phase}
                                  size="small"
                                  color={
                                    phase === "elimination" ? "error" :
                                    phase === "introduction" ? "warning" :
                                    phase === "maintenance" ? "success" : "default"
                                  }
                                  variant="outlined"
                                />
                              ))
                            ) : (
                              <Chip 
                                label={prompt.applicableDiets?.includes("traditional") && !prompt.applicableDiets?.includes("low_fodmap") ? "No Phases" : "All Phases"} 
                                size="small" 
                                color="default" 
                                variant="outlined" 
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                  <TableCell>
                    <Chip
                      label={prompt.priority || 1}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={prompt.isEnabled}
                      onChange={() => handleToggleStatus(prompt._id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(prompt.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View">
                      <IconButton onClick={() => openViewModal(prompt)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEditModal(prompt)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(prompt._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>

        {/* Add/Edit Modal */}
        <Dialog open={openModal} onClose={closeModal} maxWidth="lg" fullWidth>
          <DialogTitle>
            {editingPrompt ? "Edit Diet Prompt" : "Add New Diet Prompt"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              {/* Phase Logic Info */}
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>💡 Phase Logic:</strong> Traditional diet prompts work for all users without phase restrictions. 
                  Low FODMAP diet prompts can be phase-specific (elimination, introduction, maintenance) for targeted guidance.
                </Typography>
              </Alert>
              
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>🔄 Parallel System:</strong> This new prompt will work alongside your existing backend prompts. 
                  Existing functionality remains unchanged - this is an enhancement layer that can be enabled/disabled.
                </Typography>
              </Alert>
              
              <TextField
                label="Prompt Name"
                value={formData.promptName}
                onChange={(e) => handleInputChange("promptName", e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                fullWidth
                multiline
                rows={2}
                required
              />

              {/* Diet Configuration */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Diet Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Applicable Diet Types</InputLabel>
                    <Select
                      multiple
                      value={formData.applicableDiets}
                      onChange={(e) => {
                        const selectedDiets = e.target.value;
                        // Smart defaults: Traditional diet doesn't need phases
                        if (selectedDiets.includes("traditional") && !selectedDiets.includes("low_fodmap")) {
                          handleInputChange("applicablePhases", []);
                        }
                        handleInputChange("applicableDiets", selectedDiets);
                      }}
                      label="Applicable Diet Types"
                    >
                      <MenuItem value="traditional">Traditional</MenuItem>
                      <MenuItem value="low_fodmap">Low FODMAP</MenuItem>
                    </Select>
                    <Typography variant="caption" color="textSecondary">
                      Select which diet types can use this prompt
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Applicable Phases</InputLabel>
                    <Select
                      multiple
                      value={formData.applicablePhases}
                      onChange={(e) => handleInputChange("applicablePhases", e.target.value)}
                      label="Applicable Phases"
                      disabled={formData.applicableDiets.includes("traditional") && !formData.applicableDiets.includes("low_fodmap")}
                    >
                      <MenuItem value="elimination">Elimination</MenuItem>
                      <MenuItem value="introduction">Introduction</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                    </Select>
                    <Typography variant="caption" color="textSecondary">
                      {formData.applicableDiets.includes("traditional") && !formData.applicableDiets.includes("low_fodmap") 
                        ? "Traditional diet doesn't use phases - prompt works for all users"
                        : "Select specific phases or leave empty for all phases"
                      }
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                    label="Priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => handleInputChange("priority", parseInt(e.target.value))}
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Higher number = higher priority (1-10). Used when multiple prompts match."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Phase Logic:</strong> 
                      {formData.applicableDiets.includes("traditional") && !formData.applicableDiets.includes("low_fodmap") 
                        ? " Traditional diet prompts work for all users (no phase restrictions)."
                        : " Low FODMAP prompts can be phase-specific or work across all phases."
                      }
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>

              <TextField
                label="Prompt Template"
                value={formData.promptTemplate}
                onChange={(e) => handleInputChange("promptTemplate", e.target.value)}
                fullWidth
                multiline
                rows={10}
                required
                helperText="Write your AI prompt here. You can use variables like ${dietType}, ${phase}, ${userAge} etc."
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={formData.isEnabled}
                  onChange={(e) => handleInputChange("isEnabled", e.target.checked)}
                />
                <Typography>Enabled</Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingPrompt ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Modal */}
        <Dialog open={viewModal} onClose={() => setViewModal(false)} maxWidth="lg" fullWidth>
          <DialogTitle>View Diet Prompt</DialogTitle>
          <DialogContent>
            {viewingPrompt && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <Typography variant="h6">{viewingPrompt.promptName}</Typography>
                <Typography variant="body1">{viewingPrompt.description}</Typography>
                
                                  <Typography variant="h6">Diet Configuration</Typography>
                 <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                   <Typography variant="subtitle2">Diet Types: </Typography>
                   {viewingPrompt.applicableDiets?.map((diet) => (
                     <Chip
                       key={diet}
                       label={diet}
                       size="small"
                       color={diet === "low_fodmap" ? "primary" : "secondary"}
                     />
                   ))}
                 </Box>
                 <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                   <Typography variant="subtitle2">Phases: </Typography>
                   {viewingPrompt.applicablePhases?.length > 0 ? (
                     viewingPrompt.applicablePhases.map((phase) => (
                       <Chip
                         key={phase}
                         label={phase}
                         size="small"
                         color={
                           phase === "elimination" ? "error" :
                           phase === "introduction" ? "warning" :
                           phase === "maintenance" ? "success" : "default"
                         }
                       />
                     ))
                   ) : (
                     <Chip label="All Phases" size="small" color="default" />
                   )}
                 </Box>
                 <Box sx={{ display: "flex", gap: 1 }}>
                   <Typography variant="subtitle2">Priority: </Typography>
                   <Chip
                     label={viewingPrompt.priority || 1}
                     size="small"
                     color="primary"
                   />
                 </Box>

                <Typography variant="h6">Prompt Template</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", backgroundColor: "#f5f5f5", p: 2, borderRadius: 1 }}>
                  {viewingPrompt.promptTemplate}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default DietPromptManagement; 